/**
 * Module 6C.4 Layer 2.1 — Billing & Plan card for /settings.
 *
 * Renders four surfaces conditionally:
 *   1. Current plan header (label, trial countdown, usage meter)
 *   2. Manage subscription (Studio/Pro only) — next charge, cancel path
 *   3. Plan comparison (Trial/Free) — Free / Studio / Pro × Monthly/Yearly
 *   4. Choose-tier buttons that reuse openRazorpayCheckout() + webhook
 *      activation — same code path as the 402-triggered modal.
 *
 * The FE never decides plan state; the webhook does. Post-checkout we
 * poll /billing/usage until the plan flips, or fall back to /reconcile.
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

import {
  fetchBillingUsage, fetchBillingPlans, cancelSubscription,
  openRazorpayCheckout, waitForActivation, formatLimit, trialDaysLeft,
} from "@/lib/billing";
import { fmtDate } from "@/lib/leadUtils";


// ─── shared styles (mirrors Settings.jsx patterns) ─────────────────────────

const CARD_STYLE = {
  background: "#FFFFFF",
  border: "1px solid #E9E2D6",
  borderRadius: 10,
  padding: "18px 22px",
  marginBottom: 20,
  boxShadow: "0 2px 6px rgba(60,46,20,.04)",
};

const KICKER_STYLE = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.13em",
  textTransform: "uppercase", color: "#B08D55", marginBottom: 5,
};

const PRIMARY_BTN = {
  padding: "8px 16px", minHeight: 34, fontSize: 12, fontWeight: 600,
  letterSpacing: "0.04em",
  background: "#22382C", color: "#F4EFE4",
  border: "1px solid #22382C", borderRadius: 7,
  cursor: "pointer", transition: "background .15s, border-color .15s",
};

const GHOST_BTN = {
  padding: "8px 14px", minHeight: 34, fontSize: 12, fontWeight: 600,
  letterSpacing: "0.04em",
  background: "#FFFFFF", color: "#2A2722",
  border: "1px solid #E4DCCD", borderRadius: 7,
  cursor: "pointer",
};


// ─── main component ────────────────────────────────────────────────────────

export default function BillingCard() {
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState(null);
  const [busyTier, setBusyTier] = useState(null);  // 'studio' | 'pro' | null
  const [cadence, setCadence] = useState("monthly");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [u, p] = await Promise.all([fetchBillingUsage(), fetchBillingPlans()]);
    setUsage(u);
    setPlans(p);
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("lead_studio:plan_changed", onChange);
    return () => window.removeEventListener("lead_studio:plan_changed", onChange);
  }, [refresh]);

  const onChooseTier = async (tier) => {
    setBusyTier(tier);
    try {
      const response = await openRazorpayCheckout({ tier, cadence });
      if (!response) { setBusyTier(null); return; }
      toast.message("Activating your plan…");
      const snap = await waitForActivation(tier);
      if (snap) {
        toast.success(`You're on ${snap.label}. Welcome aboard.`);
        window.dispatchEvent(new CustomEvent("lead_studio:plan_changed", { detail: snap }));
        refresh();
      } else {
        toast.error("Payment captured but activation is delayed — refresh in a minute.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't start checkout.");
    } finally {
      setBusyTier(null);
    }
  };

  const onConfirmCancel = async () => {
    setCancelBusy(true);
    try {
      const res = await cancelSubscription();
      if (res?.already_requested) {
        toast.message("Cancellation was already scheduled.");
      } else {
        toast.success("Cancellation scheduled — access continues until period end.");
      }
      setCancelOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed. Try again or contact support.");
    } finally {
      setCancelBusy(false);
    }
  };

  if (!usage) {
    return (
      <div id="billing" data-testid="billing-card-loading" style={CARD_STYLE}>
        <div style={{ fontSize: 13, color: "#76705F" }}>Loading billing…</div>
      </div>
    );
  }

  const isTrial   = usage.plan === "trial";
  const isFree    = usage.plan === "free";
  const isPaid    = usage.plan === "studio" || usage.plan === "pro";
  const days      = trialDaysLeft(usage);
  const showChoose = isTrial || isFree;

  return (
    <>
      {/* ── Current plan card ── */}
      <div id="billing" data-testid="billing-card-current" style={CARD_STYLE}>
        <div style={KICKER_STYLE}>BILLING &amp; PLAN</div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{
              fontFamily: "var(--ds-display-font, serif)",
              fontSize: 26, color: "#22382C", lineHeight: 1.1, marginBottom: 4,
            }} data-testid="billing-current-plan-label">
              {usage.label}
              {isTrial && days !== null && (
                <span style={{ fontSize: 13, color: "#B08D55", marginLeft: 10, fontFamily: "inherit" }}>
                  · {days} day{days === 1 ? "" : "s"} left
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#76705F", lineHeight: 1.5, marginBottom: 12 }}>
              {isTrial && "Unlimited leads and projects during your 15-day trial. After trial, your studio falls back to Free unless you pick a plan."}
              {isFree && `Limited to ${usage.leads.limit} leads/month and ${usage.projects.limit} active projects. Bulk imports locked.`}
              {isPaid && "Your subscription is active. Manage cadence, next charge, and cancellation below."}
              {usage.plan === "grandfathered" && "You're on a legacy grandfathered plan — unlimited on everything."}
            </div>
            {/* Usage row: only meaningful for free + paid plans */}
            {(isFree || isPaid) && (
              <div
                style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
                  fontSize: 12, color: "#2A2722",
                }}
                data-testid="billing-usage-row"
              >
                <div>
                  <div style={{ fontSize: 9, letterSpacing: "0.18em", color: "#A39A8B", marginBottom: 2 }}>LEADS</div>
                  <div style={{ fontWeight: 500 }} data-testid="billing-leads-usage">
                    {formatLimit(usage.leads.used, usage.leads.limit, usage.leads.window)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, letterSpacing: "0.18em", color: "#A39A8B", marginBottom: 2 }}>PROJECTS</div>
                  <div style={{ fontWeight: 500 }} data-testid="billing-projects-usage">
                    {formatLimit(usage.projects.used, usage.projects.limit, usage.projects.window)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Manage subscription (paid only) ── */}
      {isPaid && (
        <ManageSubscriptionCard
          usage={usage}
          onCancelClick={() => setCancelOpen(true)}
        />
      )}

      {/* ── Plan comparison + choose (trial/free only) ── */}
      {showChoose && plans && (
        <div data-testid="billing-plan-comparison" style={CARD_STYLE}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
            <div style={KICKER_STYLE}>CHOOSE A PLAN</div>
            <CadenceToggle value={cadence} onChange={setCadence} />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 14,
            }}
          >
            {plans.map((p) => (
              <PlanColumn
                key={p.key}
                plan={p}
                cadence={cadence}
                isCurrent={p.key === "free" && isFree}
                onChoose={p.key === "free" ? null : () => onChooseTier(p.key)}
                busy={busyTier === p.key}
                disabled={busyTier !== null && busyTier !== p.key}
              />
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#A39A8B", marginTop: 12, lineHeight: 1.5 }}>
            All plans include unlimited team seats. Yearly billing saves ~17%. Cancel anytime — access continues until the end of your billing period.
          </div>
        </div>
      )}

      {/* ── Cancel confirm modal ── */}
      {cancelOpen && (
        <CancelConfirmModal
          usage={usage}
          busy={cancelBusy}
          onConfirm={onConfirmCancel}
          onCancel={() => setCancelOpen(false)}
        />
      )}
    </>
  );
}


// ─── sub-components ────────────────────────────────────────────────────────

function CadenceToggle({ value, onChange }) {
  const opts = [
    { v: "monthly", l: "Monthly" },
    { v: "yearly",  l: "Yearly · save ~17%" },
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        background: "#F4EFE4",
        border: "1px solid #E4DCCD",
        borderRadius: 999,
        padding: 3,
      }}
      data-testid="billing-cadence-toggle"
    >
      {opts.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          data-testid={`billing-cadence-${o.v}`}
          style={{
            padding: "6px 14px", fontSize: 11, fontWeight: 600,
            letterSpacing: "0.05em",
            background: value === o.v ? "#22382C" : "transparent",
            color: value === o.v ? "#F4EFE4" : "#76705F",
            border: "none", borderRadius: 999,
            cursor: "pointer",
            transition: "background .15s, color .15s",
          }}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}


function PlanColumn({ plan, cadence, isCurrent, onChoose, busy, disabled }) {
  const price = cadence === "yearly" ? plan.yearly_inr : plan.monthly_inr;
  const isFreePlan = plan.key === "free";
  const priceLabel = (isFreePlan || price == null || price === 0)
    ? "Free"
    : `₹${price.toLocaleString("en-IN")}`;
  const priceSuffix = (isFreePlan || price == null || price === 0)
    ? ""
    : cadence === "yearly" ? "/yr" : "/mo";
  const features = [];
  if (plan.leads_limit == null) {
    features.push("Unlimited leads");
  } else {
    const window = plan.leads_window === "calendar_month" ? "month" : "year";
    features.push(`${plan.leads_limit.toLocaleString("en-IN")} leads / ${window}`);
  }
  if (plan.projects_limit == null) {
    features.push("Unlimited projects");
  } else if (plan.projects_window === "live") {
    features.push(`${plan.projects_limit} active projects`);
  } else {
    features.push(`${plan.projects_limit} projects / year`);
  }
  features.push(plan.import_enabled ? "Bulk imports (Sheets, CSV, WedMeGood)" : "Manual lead entry only");
  features.push("Unlimited team seats");
  features.push("Priority email support");

  return (
    <div
      data-testid={`billing-plan-${plan.key}`}
      style={{
        border: isCurrent ? "1px solid #22382C" : "1px solid #E9E2D6",
        borderRadius: 10,
        padding: "18px 16px",
        background: isCurrent ? "#FAF6EC" : "#FFFFFF",
        display: "flex", flexDirection: "column", gap: 10,
        position: "relative",
      }}
    >
      {isCurrent && (
        <div
          style={{
            position: "absolute", top: -9, right: 12,
            fontSize: 9, fontWeight: 700, letterSpacing: "0.16em",
            textTransform: "uppercase",
            background: "#22382C", color: "#F4EFE4",
            padding: "2px 8px", borderRadius: 999,
          }}
          data-testid={`billing-plan-${plan.key}-current`}
        >
          Current
        </div>
      )}
      <div>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.16em",
          textTransform: "uppercase", color: "#B08D55", marginBottom: 4,
        }}>
          {plan.label}
        </div>
        <div style={{ fontSize: 26, color: "#22382C", fontWeight: 500, lineHeight: 1.1 }}>
          {priceLabel}
          <span style={{ fontSize: 12, color: "#76705F", fontWeight: 400 }}> {priceSuffix}</span>
        </div>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 6 }}>
        {features.map((f, i) => (
          <li key={i} style={{ fontSize: 12, color: "#2A2722", display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.4 }}>
            <Check size={13} strokeWidth={2.4} style={{ color: "#22382C", marginTop: 2, flexShrink: 0 }} />
            {f}
          </li>
        ))}
      </ul>
      <div style={{ marginTop: "auto", paddingTop: 12 }}>
        {onChoose ? (
          <button
            onClick={onChoose}
            disabled={disabled || busy}
            data-testid={`billing-choose-${plan.key}`}
            style={{
              ...PRIMARY_BTN,
              width: "100%",
              opacity: (disabled || busy) ? 0.6 : 1,
              cursor: (disabled || busy) ? "not-allowed" : "pointer",
              display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 8,
            }}
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            {busy ? "Opening…" : `Choose ${plan.label}`}
          </button>
        ) : (
          <div
            style={{
              ...GHOST_BTN,
              width: "100%", textAlign: "center",
              opacity: 0.7, cursor: "default",
              background: "#F4EFE4",
            }}
            data-testid={`billing-choose-${plan.key}-disabled`}
          >
            {isCurrent ? "Your current plan" : "—"}
          </div>
        )}
      </div>
    </div>
  );
}


function ManageSubscriptionCard({ usage, onCancelClick }) {
  const sub = usage.subscription || {};
  const cadenceLabel = sub.cadence === "yearly" ? "Yearly" : sub.cadence === "monthly" ? "Monthly" : "—";
  const cancelRequested = Boolean(sub.cancel_requested_at);
  const activatedLine = sub.activated_at ? fmtDate(sub.activated_at) : "—";
  const nextChargeLine = sub.current_period_end ? fmtDate(sub.current_period_end) : "—";
  const lastChargedLine = sub.last_charged_at ? fmtDate(sub.last_charged_at) : "—";

  return (
    <div data-testid="billing-manage-card" style={CARD_STYLE}>
      <div style={KICKER_STYLE}>MANAGE SUBSCRIPTION</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 14, marginBottom: 14,
        }}
      >
        <MetaCell label="Cadence" value={cadenceLabel} testid="mgmt-cadence" />
        <MetaCell label="Next charge" value={nextChargeLine} testid="mgmt-next-charge" />
        <MetaCell label="Last charged" value={lastChargedLine} testid="mgmt-last-charged" />
        <MetaCell label="Activated" value={activatedLine} testid="mgmt-activated" />
      </div>
      {cancelRequested ? (
        <div
          data-testid="billing-cancel-scheduled"
          style={{
            padding: "10px 12px",
            background: "#F4EDDF", color: "#9A7A3C",
            border: "1px solid #EEDFC0", borderRadius: 6,
            fontSize: 12, lineHeight: 1.5,
          }}
        >
          Cancellation scheduled on {fmtDate(sub.cancel_requested_at)}. Your access continues until {nextChargeLine}, after which you&apos;ll be moved to the Free plan. Contact support to undo.
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onCancelClick}
            data-testid="billing-cancel-btn"
            style={{
              ...GHOST_BTN,
              color: "#A35C45",
              borderColor: "#E9D2C7",
            }}
          >
            Cancel subscription
          </button>
        </div>
      )}
    </div>
  );
}


function MetaCell({ label, value, testid }) {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: "0.18em", color: "#A39A8B", marginBottom: 2 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 13, color: "#2A2722", fontWeight: 500 }} data-testid={testid}>{value}</div>
    </div>
  );
}


function CancelConfirmModal({ usage, busy, onConfirm, onCancel }) {
  const periodEnd = usage.subscription?.current_period_end
    ? fmtDate(usage.subscription.current_period_end)
    : "the end of your current billing period";
  return (
    <div
      data-testid="billing-cancel-modal"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)", backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF", maxWidth: 460, width: "100%",
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #A35C45",
          borderRadius: 10,
          boxShadow: "0 18px 40px rgba(34,56,44,.18)",
          padding: 24,
        }}
      >
        <div style={{ ...KICKER_STYLE, color: "#A35C45", marginBottom: 6 }}>CANCEL SUBSCRIPTION</div>
        <div style={{ fontFamily: "var(--ds-display-font, serif)", fontSize: 22, color: "#22382C", marginBottom: 10, lineHeight: 1.15 }}>
          Cancel {usage.label}?
        </div>
        <div style={{ fontSize: 13, color: "#76705F", lineHeight: 1.55, marginBottom: 4 }}>
          Your subscription will end on <strong style={{ color: "#22382C" }}>{periodEnd}</strong>.
          Between now and then, everything continues to work — leads, projects, imports, and team access.
        </div>
        <div style={{ fontSize: 13, color: "#76705F", lineHeight: 1.55, marginBottom: 4 }}>
          After that, your studio moves to the Free plan (5 leads/month, 2 active projects, no imports).
          Your data is never deleted.
        </div>
        <div style={{ fontSize: 12, color: "#A39A8B", lineHeight: 1.55, marginBottom: 18 }}>
          Change your mind? Contact support before the period ends.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid="billing-cancel-modal-back"
            style={{ ...GHOST_BTN, opacity: busy ? 0.6 : 1 }}
          >
            Keep subscription
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="billing-cancel-modal-confirm"
            style={{
              padding: "8px 16px", minHeight: 34, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.04em", color: "#FFFFFF",
              background: busy ? "#C7867A" : "#A35C45",
              border: `1px solid ${busy ? "#C7867A" : "#A35C45"}`,
              borderRadius: 7,
              cursor: busy ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            {busy ? "Cancelling…" : "Yes, cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
